'use client';

import { COLORS, PROFILE_NAV_BAR } from '@/constants';
import { useAuth } from '@/contexts';
import { isProd } from '@/common';
import { useTranslations } from 'next-intl';
import { FC, ReactNode } from 'react';
import {
  Container,
  MainBox,
  MainContentBox,
  NavigationMenuContainer,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuWrapper,
  NavigationSectionHeader,
  PageBackground,
  SidebarContactBoxWrapper,
} from './styled';
import { Breadcrumbs, Typography } from '@mui/material';
import Link from 'next/link';
import { SellProperty } from '@/features/components';
import { useRouter } from 'next/navigation';
import { usePathname } from '@/config/i18n/navigation';
import { LogoutIcon } from '@/components';
import { PATHS } from '@/constants/paths';
import { WlasContactBox } from '@/features/pages/profile/wlas-widget/components';
import { isMasterAdmin } from '@/contexts/auth/isMasterAdmin';
import { MasterAdminSidebarBlock } from './components/MasterAdminSidebarBlock';

type ProfileLayoutProps = {
  children: ReactNode;
  bg?: string;
};

const PAGE_BG: Record<string, string> = {
  [PATHS.DASHBOARD]: COLORS['beige-404'],
  [PATHS.MEMBER_PROFILE]: COLORS['beige-404'],
  [PATHS.WLAS_WIDGET]: COLORS['beige-404'],
};

const ADMIN_ONLY_PATHS = new Set([
  PATHS.DASHBOARD,
  PATHS.MEMBER_PROFILE,
  PATHS.WLAS_WIDGET,
  PATHS.MASTER_ADMIN,
]);

const SELL_BANNER_PATHS = new Set([PATHS.WATCH_LIST, PATHS.SEARCH_PROFILE]);

export const ProfileLayout: FC<ProfileLayoutProps> = ({ children, bg }) => {
  const t = useTranslations();

  const { logout, user } = useAuth();
  const navItems = PROFILE_NAV_BAR.filter(
    (e) =>
      (!ADMIN_ONLY_PATHS.has(e.title as Parameters<typeof ADMIN_ONLY_PATHS.has>[0]) ||
        user?.role === 'admin' ||
        user?.role === 'broker') &&
      !(e.hiddenInProd && isProd),
  );

  const router = useRouter();
  const pathname = usePathname();

  const pathSegments = pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[1];

  const resolvedBg = bg ?? PAGE_BG[lastSegment];
  const showSellBanner = SELL_BANNER_PATHS.has(
    lastSegment as Parameters<typeof SELL_BANNER_PATHS.has>[0],
  );

  const handleNavigate = (title: string, isCurrent: boolean) => {
    if (!isCurrent) router.push(`/profile/${title}`);
  };

  return (
    <PageBackground $bg={resolvedBg}>
      <Container>
        <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ px: '30px' }}>
          <Link href={'/'}>
            <Typography color={COLORS['primary/600']}>{t('navigation.home')}</Typography>
          </Link>
          <Typography textTransform="capitalize">
            {lastSegment === PATHS.WLAS_WIDGET
              ? t('wlasWidget.breadcrumb')
              : lastSegment
                ? t(`navigation.${lastSegment}`)
                : ''}
          </Typography>
        </Breadcrumbs>

        <MainBox>
          <MainContentBox>{children}</MainContentBox>
          <NavigationMenuWrapper>
            {isMasterAdmin(user?.role) && <MasterAdminSidebarBlock />}
            <NavigationMenuContainer>
              <NavigationSectionHeader>{t('navigation.account')}</NavigationSectionHeader>
              <NavigationMenuList>
                {navItems.map((e, i) => {
                  const { icon, title } = e;
                  const isCurrent = title === lastSegment;

                  return (
                    <div key={i} onClick={() => handleNavigate(title, isCurrent)}>
                      <NavigationMenuItem $isActive={isCurrent}>
                        {icon}
                        <Typography fontWeight={600}>{t(`navigation.${title}`)}</Typography>
                      </NavigationMenuItem>
                    </div>
                  );
                })}

                <NavigationMenuItem onClick={logout}>
                  <LogoutIcon />
                  <Typography fontWeight={600}>{t(`auth.logout`)}</Typography>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenuContainer>
            {lastSegment === PATHS.WLAS_WIDGET && (
              <SidebarContactBoxWrapper>
                <WlasContactBox />
              </SidebarContactBoxWrapper>
            )}
          </NavigationMenuWrapper>
        </MainBox>
        {showSellBanner && <SellProperty />}
      </Container>
    </PageBackground>
  );
};
